import { ensureGoogleLibrary } from "./googleMapsLoader";
import { googleRegion } from "../validation/laenderprofil";

/**
 * Ergänzt eine Anschrift um die fehlende Hälfte von PLZ und Ort — über den
 * Geocoder der Google-Maps-JS-API (derselbe Key wie die Adresssuche).
 *
 * Anlass (Fachabstimmung 10/2026): „PLZ wird nicht completed". Das
 * Adress-Autocomplete füllt die PLZ nur, wenn Google sie im gewählten Treffer
 * mitliefert — bei Vorschlägen ohne Hausnummer oder bei frei getippter Straße
 * blieb das Feld leer. Zwei Wege:
 *
 * - **PLZ fehlt, Straße und Ort stehen** → Adresse geokodieren, PLZ lesen.
 * - **Ort fehlt, PLZ steht** → nach der PLZ im gewählten Land suchen, Ort lesen.
 *
 * Wirft nie und liefert `{}`, wenn kein Key gesetzt ist oder Google nichts
 * Eindeutiges findet — die Felder bleiben dann einfach, wie sie sind.
 */

export type PlzOrtEingabe = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

export type PlzOrtErgaenzung = { postalCode?: string; city?: string };

const cache = new Map<string, PlzOrtErgaenzung>();

/** Nur für Tests. */
export function _resetPlzOrtCacheFuerTests(): void {
  cache.clear();
}

type Komponente = { long_name?: string; longText?: string; types?: string[] };

const komponente = (comps: Komponente[], typ: string): string => {
  const c = comps.find((k) => (k.types ?? []).includes(typ));
  return (c?.long_name ?? c?.longText ?? "").trim();
};

/** Welche Anfrage wäre sinnvoll? `null`, wenn nichts zu ergänzen ist. */
export function plzOrtAnfrage(
  e: PlzOrtEingabe,
): { art: "plz" | "ort"; request: Record<string, unknown> } | null {
  const region = googleRegion(e.country);
  const plz = e.postalCode.trim();
  const ort = e.city.trim();
  const strasse = e.street.trim();
  if (!plz && strasse && ort) {
    return {
      art: "plz",
      request: {
        address: `${strasse}, ${ort}`,
        componentRestrictions: { country: region },
        region,
      },
    };
  }
  if (plz && !ort) {
    return {
      art: "ort",
      request: { componentRestrictions: { postalCode: plz, country: region }, region },
    };
  }
  return null;
}

/** Liest die Ergänzung aus der Geocoder-Antwort (reine Funktion, testbar). */
export function leseErgaenzung(art: "plz" | "ort", results: unknown): PlzOrtErgaenzung {
  const liste = Array.isArray(results) ? results : [];
  const erster = liste[0] as { address_components?: Komponente[]; partial_match?: boolean } | undefined;
  if (!erster) return {};
  const comps = erster.address_components ?? [];
  if (art === "plz") {
    // Bei mehreren Treffern mit unterschiedlicher PLZ (lange Straßen über
    // mehrere Bezirke) lieber nichts eintragen als die falsche.
    const plzs = new Set(
      liste
        .map((r) => komponente((r as { address_components?: Komponente[] }).address_components ?? [], "postal_code"))
        .filter(Boolean),
    );
    const plz = komponente(comps, "postal_code");
    return plz && plzs.size === 1 ? { postalCode: plz } : {};
  }
  const ort =
    komponente(comps, "locality") ||
    komponente(comps, "postal_town") ||
    komponente(comps, "administrative_area_level_3");
  return ort ? { city: ort } : {};
}

export async function ergaenzePlzOrt(e: PlzOrtEingabe): Promise<PlzOrtErgaenzung> {
  const anfrage = plzOrtAnfrage(e);
  if (!anfrage) return {};
  const key = JSON.stringify(anfrage.request).toLowerCase();
  const treffer = cache.get(key);
  if (treffer) return treffer;
  const libPromise = ensureGoogleLibrary("geocoding");
  if (!libPromise) return {};
  try {
    const lib = await libPromise;
    if (!lib || typeof lib.Geocoder !== "function") return {};
    const antwort = await new lib.Geocoder().geocode(anfrage.request);
    const ergebnis = leseErgaenzung(anfrage.art, antwort?.results);
    if (Object.keys(ergebnis).length > 0) cache.set(key, ergebnis);
    return ergebnis;
  } catch {
    return {};
  }
}
